import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { IcnCheck, IcnChevron } from '../../icons/index.js';
import { ensurePortalRoot } from '../shared/index.js';
import { useFloating } from '../../hooks/useFloating.js';
import type {
  MenuEntry,
  MenuItemDescriptor,
  MenuLeafEntry,
  MenuSubmenuDescriptor,
} from './types.js';

export interface MenuProps {
  items: readonly MenuEntry[];
  /** Called when any leaf item is activated. Fires after `item.onSelect`. */
  onItemSelect?: (item: MenuItemDescriptor) => void;
  /** Controlled highlight (for ContextMenu etc. that want arrow nav externally). */
  highlightedId?: string | null;
  onHighlightChange?: (id: string | null) => void;
  /** aria-labelledby for the menu region. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Typeahead is on by default (letter keys jump highlight). */
  typeahead?: boolean;
  /** Auto-focus the first enabled item on mount. Default true. */
  autoFocus?: boolean;
  /** Announced when user lands on an item during arrow nav. */
  'data-side'?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  ref?: Ref<HTMLDivElement>;
  /** When this Menu was opened as a child submenu, the parent passes
   *  `onClose` so ArrowLeft / Escape collapses just this layer (rather
   *  than dismissing the whole cascade). Internal — not part of the
   *  public consumer-facing API. */
  onClose?: () => void;
}

interface FlattenedRow {
  readonly descriptor: MenuLeafEntry;
}

const TYPEAHEAD_TIMEOUT = 1000;
/** Hover-intent delay for opening a submenu — short enough to feel
 *  responsive, long enough that brushing past a row doesn't open it. */
const SUBMENU_OPEN_DELAY = 140;
/** Close delay so the user can travel diagonally from the parent row to
 *  the submenu without losing it. */
const SUBMENU_CLOSE_DELAY = 90;

/** Self-developed menu. Renders `role="menu"` with proper APG keyboard
 *  semantics:
 *    - ↑/↓ navigate enabled items
 *    - Home/End jump to first/last
 *    - Letters typeahead (1 s buffer)
 *    - Enter/Space activate (or open submenu when on a submenu row)
 *    - → opens a submenu when on a submenu row + transfers focus
 *    - ← in a submenu collapses it + restores focus to the parent row
 *    - Esc surfaced via onKeyDown so the parent (Popover/ContextMenu)
 *      can dismiss the cascade; submenus collapse just themselves
 *
 *  This is a layout primitive. It does NOT own open/close state for the
 *  outer container; wrap it in DropdownMenu / ContextMenu / CommandPalette
 *  for that. Submenu open/close state IS owned here, since cascading is
 *  inherent to the menu shape rather than the host. */
export function Menu(props: MenuProps): ReactNode {
  const {
    items,
    onItemSelect,
    highlightedId: controlledHighlight,
    onHighlightChange,
    typeahead = true,
    autoFocus = true,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'data-side': side,
    className,
    ref,
    onClose,
  } = props;

  const reactId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLButtonElement>());
  const typeaheadState = useRef<{ buffer: string; timer: number | null }>({
    buffer: '',
    timer: null,
  });

  // Flatten groups into a single ordered list of focusable leaves
  // (items + submenus). Separators and group containers are skipped.
  const flat = useMemo<readonly FlattenedRow[]>(() => {
    const out: FlattenedRow[] = [];
    const walk = (entries: readonly MenuEntry[]): void => {
      for (const e of entries) {
        if (e.kind === 'item' || e.kind === 'submenu') {
          out.push({ descriptor: e });
        } else if (e.kind === 'group') {
          walk(e.items);
        }
      }
    };
    walk(items);
    return out;
  }, [items]);

  const [uncontrolledHighlight, setUncontrolledHighlight] = useState<string | null>(() => {
    const first = flat.find((f) => f.descriptor.disabled !== true);
    return first?.descriptor.id ?? null;
  });
  const highlightedId = controlledHighlight ?? uncontrolledHighlight;

  const setHighlighted = useCallback(
    (id: string | null) => {
      if (controlledHighlight === undefined) setUncontrolledHighlight(id);
      onHighlightChange?.(id);
    },
    [controlledHighlight, onHighlightChange],
  );

  const findEnabledFrom = useCallback(
    (currentId: string | null, direction: 1 | -1): string | null => {
      if (flat.length === 0) return null;
      const currentIdx =
        currentId === null ? -1 : flat.findIndex((f) => f.descriptor.id === currentId);
      const len = flat.length;
      for (let step = 1; step <= len; step += 1) {
        const nextIdx = (((currentIdx + direction * step) % len) + len) % len;
        const entry = flat[nextIdx];
        if (entry !== undefined && entry.descriptor.disabled !== true) return entry.descriptor.id;
      }
      return null;
    },
    [flat],
  );

  // -- Submenu state --------------------------------------------------
  // openSubmenuId: which submenu (if any) is currently expanded.
  // Hover-intent timers smooth the open/close so a quick mouse swipe
  // doesn't pop multiple submenus, and diagonal travel into the open
  // submenu doesn't accidentally close it.
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const openSubmenuNow = useCallback((id: string) => {
    clearHoverTimer();
    setOpenSubmenuId(id);
  }, [clearHoverTimer]);

  const scheduleOpenSubmenu = useCallback(
    (id: string) => {
      if (openSubmenuId === id) {
        clearHoverTimer();
        return;
      }
      clearHoverTimer();
      hoverTimerRef.current = window.setTimeout(() => {
        setOpenSubmenuId(id);
        hoverTimerRef.current = null;
      }, SUBMENU_OPEN_DELAY);
    },
    [openSubmenuId, clearHoverTimer],
  );

  const scheduleCloseSubmenu = useCallback(() => {
    clearHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      setOpenSubmenuId(null);
      hoverTimerRef.current = null;
    }, SUBMENU_CLOSE_DELAY);
  }, [clearHoverTimer]);

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  // If the highlighted item is a submenu and is hovered/keyboard-active,
  // we want it to be marked aria-expanded only while open. If the
  // highlight moves OFF the open submenu's trigger row, schedule the
  // close — unless the user is now hovering an item INSIDE the submenu
  // (handled by the submenu itself via onMouseEnter cancellation).
  // -------------------------------------------------------------------

  const activate = useCallback(
    (entry: MenuLeafEntry) => {
      if (entry.disabled === true) return;
      if (entry.kind === 'submenu') {
        openSubmenuNow(entry.id);
        return;
      }
      entry.onSelect?.();
      onItemSelect?.(entry);
    },
    [onItemSelect, openSubmenuNow],
  );

  const typeaheadMatch = useCallback(
    (char: string) => {
      const state = typeaheadState.current;
      if (state.timer !== null) window.clearTimeout(state.timer);
      state.buffer += char.toLocaleLowerCase();
      state.timer = window.setTimeout(() => {
        state.buffer = '';
        state.timer = null;
      }, TYPEAHEAD_TIMEOUT);
      const startIdx =
        highlightedId === null ? -1 : flat.findIndex((f) => f.descriptor.id === highlightedId);
      const len = flat.length;
      for (let step = 1; step <= len; step += 1) {
        const idx = (((startIdx + step) % len) + len) % len;
        const entry = flat[idx];
        if (entry === undefined || entry.descriptor.disabled === true) continue;
        const text = textOf(entry.descriptor).toLocaleLowerCase();
        if (text.startsWith(state.buffer)) {
          setHighlighted(entry.descriptor.id);
          return;
        }
      }
    },
    [flat, highlightedId, setHighlighted],
  );

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const { key } = e;
    if (key === 'ArrowDown' || key === 'Down') {
      e.preventDefault();
      setHighlighted(findEnabledFrom(highlightedId, 1));
      setOpenSubmenuId(null);
      clearHoverTimer();
      return;
    }
    if (key === 'ArrowUp' || key === 'Up') {
      e.preventDefault();
      setHighlighted(findEnabledFrom(highlightedId, -1));
      setOpenSubmenuId(null);
      clearHoverTimer();
      return;
    }
    if (key === 'Home') {
      e.preventDefault();
      setHighlighted(findEnabledFrom(null, 1));
      setOpenSubmenuId(null);
      clearHoverTimer();
      return;
    }
    if (key === 'End') {
      e.preventDefault();
      setHighlighted(findEnabledFrom(null, -1));
      setOpenSubmenuId(null);
      clearHoverTimer();
      return;
    }
    if (key === 'ArrowRight' || key === 'Right') {
      const hit = highlightedId !== null
        ? flat.find((f) => f.descriptor.id === highlightedId)?.descriptor
        : undefined;
      if (hit?.kind === 'submenu' && hit.disabled !== true) {
        e.preventDefault();
        openSubmenuNow(hit.id);
        return;
      }
    }
    if (key === 'ArrowLeft' || key === 'Left') {
      // Only meaningful when this Menu is rendered as a submenu — the
      // parent passes `onClose`. Otherwise let the event flow.
      if (onClose !== undefined) {
        e.preventDefault();
        onClose();
        return;
      }
    }
    if (key === 'Escape') {
      // In submenu context: collapse just this layer, keep the cascade
      // open. At root: let the host (ContextMenu) handle dismissal —
      // useEscapeStack on the host will fire.
      if (onClose !== undefined) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
    }
    if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      if (highlightedId === null) return;
      const hit = flat.find((f) => f.descriptor.id === highlightedId);
      if (hit === undefined) return;
      e.preventDefault();
      activate(hit.descriptor);
      return;
    }
    if (typeahead && key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      typeaheadMatch(key);
    }
  };

  // Auto-focus the menu region on mount so arrow keys work without a click.
  useLayoutEffect(() => {
    if (!autoFocus) return;
    rootRef.current?.focus();
  }, [autoFocus]);

  // Scroll highlighted item into view.
  useEffect(() => {
    if (highlightedId === null) return;
    const root = rootRef.current;
    if (root === null) return;
    const el = root.querySelector<HTMLElement>(`[data-item-id="${highlightedId}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightedId]);

  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref !== undefined && ref !== null) {
        (ref as { current: HTMLDivElement | null }).current = node;
      }
    },
    [ref],
  );

  const registerRowRef = useCallback((id: string, node: HTMLButtonElement | null) => {
    if (node === null) rowRefs.current.delete(id);
    else rowRefs.current.set(id, node);
  }, []);

  const closeSubmenuAndRefocusParent = useCallback(
    (parentId: string) => {
      setOpenSubmenuId(null);
      clearHoverTimer();
      // Restore focus to the menu root so arrow keys keep working, then
      // the parent button is highlighted (visual focus).
      rootRef.current?.focus({ preventScroll: true });
      setHighlighted(parentId);
    },
    [clearHoverTimer, setHighlighted],
  );

  const classes = ['vq-menu', className].filter(Boolean).join(' ');

  // Find the descriptor + trigger element for the open submenu so the
  // SubmenuPanel can anchor against it.
  const openSubmenu = openSubmenuId !== null
    ? flat.find((f) => f.descriptor.id === openSubmenuId)?.descriptor
    : undefined;
  const openSubmenuDescriptor =
    openSubmenu?.kind === 'submenu' ? openSubmenu : undefined;
  const openSubmenuTrigger =
    openSubmenuId !== null ? rowRefs.current.get(openSubmenuId) ?? null : null;

  return (
    <div
      ref={setRootRef}
      role="menu"
      tabIndex={-1}
      className={classes}
      data-component="Menu"
      data-side={side}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      onKeyDown={onKeyDown}
      // When the mouse leaves the menu entirely, schedule a close on any
      // open submenu — the submenu's own onMouseEnter will cancel the
      // close if the user has moved INTO it.
      onMouseLeave={() => {
        if (openSubmenuId !== null) scheduleCloseSubmenu();
      }}
      id={`${reactId}-menu`}
    >
      {items.map((entry) =>
        renderEntry(entry, {
          highlightedId,
          openSubmenuId,
          setHighlighted,
          activate,
          registerRowRef,
          scheduleOpenSubmenu,
          scheduleCloseSubmenu,
          clearHoverTimer,
        }),
      )}
      {openSubmenuDescriptor !== undefined && openSubmenuTrigger !== null ? (
        <SubmenuPanel
          descriptor={openSubmenuDescriptor}
          trigger={openSubmenuTrigger}
          onClose={() => {
            closeSubmenuAndRefocusParent(openSubmenuDescriptor.id);
          }}
          onMouseEnter={clearHoverTimer}
          onMouseLeave={scheduleCloseSubmenu}
          {...(onItemSelect !== undefined ? { onItemSelect } : {})}
        />
      ) : null}
    </div>
  );
}

interface RenderEntryCtx {
  readonly highlightedId: string | null;
  readonly openSubmenuId: string | null;
  readonly setHighlighted: (id: string) => void;
  readonly activate: (entry: MenuLeafEntry) => void;
  readonly registerRowRef: (id: string, node: HTMLButtonElement | null) => void;
  readonly scheduleOpenSubmenu: (id: string) => void;
  readonly scheduleCloseSubmenu: () => void;
  readonly clearHoverTimer: () => void;
}

function renderEntry(entry: MenuEntry, ctx: RenderEntryCtx): ReactNode {
  if (entry.kind === 'separator') {
    return <div key={entry.id} role="separator" className="vq-menu__separator" />;
  }
  if (entry.kind === 'group') {
    return (
      <div key={entry.id} role="group">
        {entry.label !== undefined ? (
          <div className="vq-menu__group-label">{entry.label}</div>
        ) : null}
        {entry.items.map((sub) => renderEntry(sub, ctx))}
      </div>
    );
  }
  if (entry.kind === 'submenu') return renderSubmenuRow(entry, ctx);
  return renderItemRow(entry, ctx);
}

function renderItemRow(entry: MenuItemDescriptor, ctx: RenderEntryCtx): ReactNode {
  const isHighlighted = ctx.highlightedId === entry.id;
  return (
    <button
      key={entry.id}
      type="button"
      role={entry.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
      aria-checked={entry.checked === undefined ? undefined : entry.checked}
      aria-disabled={entry.disabled === true ? true : undefined}
      disabled={entry.disabled === true}
      className="vq-menu__item"
      data-item-id={entry.id}
      data-highlighted={isHighlighted ? 'true' : undefined}
      data-disabled={entry.disabled === true ? 'true' : undefined}
      data-tone={entry.tone}
      tabIndex={-1}
      onMouseEnter={() => {
        if (entry.disabled !== true) ctx.setHighlighted(entry.id);
        // Hovering a non-submenu row should close any open sibling submenu.
        if (ctx.openSubmenuId !== null) ctx.scheduleCloseSubmenu();
      }}
      onFocus={() => {
        if (entry.disabled !== true) ctx.setHighlighted(entry.id);
      }}
      onClick={() => {
        ctx.activate(entry);
      }}
    >
      {entry.checked !== undefined ? (
        <span className="vq-menu__item-check" aria-hidden="true">
          {entry.checked ? <IcnCheck size={12} /> : null}
        </span>
      ) : (
        <span
          className="vq-menu__item-icon"
          aria-hidden="true"
          data-empty={entry.icon === undefined ? 'true' : undefined}
        >
          {entry.icon ?? null}
        </span>
      )}
      <span className="vq-menu__item-label">{entry.label}</span>
      {entry.shortcut !== undefined ? (
        <span className="vq-menu__item-shortcut">{entry.shortcut}</span>
      ) : null}
    </button>
  );
}

function renderSubmenuRow(entry: MenuSubmenuDescriptor, ctx: RenderEntryCtx): ReactNode {
  const isHighlighted = ctx.highlightedId === entry.id;
  const isOpen = ctx.openSubmenuId === entry.id;
  return (
    <button
      key={entry.id}
      ref={(node) => {
        ctx.registerRowRef(entry.id, node);
      }}
      type="button"
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      aria-disabled={entry.disabled === true ? true : undefined}
      disabled={entry.disabled === true}
      className="vq-menu__item vq-menu__item--submenu"
      data-item-id={entry.id}
      data-highlighted={isHighlighted ? 'true' : undefined}
      data-open={isOpen ? 'true' : undefined}
      data-disabled={entry.disabled === true ? 'true' : undefined}
      data-tone={entry.tone}
      tabIndex={-1}
      onMouseEnter={() => {
        if (entry.disabled === true) return;
        ctx.setHighlighted(entry.id);
        ctx.scheduleOpenSubmenu(entry.id);
      }}
      onMouseLeave={() => {
        // If we're leaving but the submenu is open, the parent's
        // onMouseLeave (on .vq-menu) handles the close decision after
        // the submenu's onMouseEnter has had a chance to cancel.
      }}
      onFocus={() => {
        if (entry.disabled !== true) ctx.setHighlighted(entry.id);
      }}
      onClick={() => {
        if (entry.disabled !== true) ctx.activate(entry);
      }}
    >
      <span
        className="vq-menu__item-icon"
        aria-hidden="true"
        data-empty={entry.icon === undefined ? 'true' : undefined}
      >
        {entry.icon ?? null}
      </span>
      <span className="vq-menu__item-label">{entry.label}</span>
      <span className="vq-menu__item-submenu-arrow" aria-hidden="true">
        <IcnChevron size={14} />
      </span>
    </button>
  );
}

interface SubmenuPanelProps {
  readonly descriptor: MenuSubmenuDescriptor;
  readonly trigger: HTMLElement;
  readonly onItemSelect?: (item: MenuItemDescriptor) => void;
  readonly onClose: () => void;
  readonly onMouseEnter: () => void;
  readonly onMouseLeave: () => void;
}

/** Floating panel that holds a child Menu, anchored to a submenu trigger
 *  row in its parent. Positioning + flip + viewport clamp delegated to
 *  `useFloating`; closing on outside interaction is the host's job (the
 *  parent Menu unmounts us when its `openSubmenuId` clears). */
function SubmenuPanel(props: SubmenuPanelProps): ReactNode {
  const { descriptor, trigger, onItemSelect, onClose, onMouseEnter, onMouseLeave } = props;
  const portalRoot = useMemo(() => ensurePortalRoot(), []);

  const triggerRect = useMemo(() => {
    const r = trigger.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }, [trigger]);

  const { setFloating, floatingStyle, maxHeight, rect } = useFloating({
    open: true,
    placement: 'right-start',
    gap: 4,
    virtualAnchor: triggerRect,
  });

  if (portalRoot === null) return null;

  return createPortal(
    <div
      ref={setFloating}
      className="vq-context-menu-portal vq-submenu-portal"
      data-component="MenuSubmenu"
      data-side={rect?.side ?? 'right'}
      style={
        {
          ...floatingStyle,
          ...(maxHeight > 0
            ? { ['--vq-menu-max-h' as string]: `${String(maxHeight)}px` }
            : {}),
        } as Record<string, string | number>
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Menu
        items={descriptor.items}
        onClose={onClose}
        autoFocus
        {...(onItemSelect !== undefined ? { onItemSelect } : {})}
        {...(typeof descriptor.label === 'string'
          ? { 'aria-label': descriptor.label }
          : {})}
        {...(rect?.side !== undefined ? { 'data-side': rect.side } : {})}
      />
    </div>,
    portalRoot,
  );
}

function textOf(item: MenuLeafEntry): string {
  if (item.searchText !== undefined) return item.searchText;
  if (typeof item.label === 'string') return item.label;
  if (typeof item.label === 'number') return String(item.label);
  return '';
}
